/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getSites} from '@app/utils/site-utils'
import {urlPartPositions} from '@app/constants'

/**
 * Configures and generates a list of route objects based on site and locale configurations.
 *
 * This function expands the provided routes array by generating new route paths for each site and locale combination,
 * according to the application's URL configuration. It also allows certain routes to be ignored from expansion.
 *
 * @param {Array<Object>} routes - The initial array of route objects, each containing at least a `path` property.
 * @param {Object} config - The configuration object containing application and URL structure settings.
 * @param {Object} options - Additional options for route configuration.
 * @param {Array<string>} [options.ignoredRoutes=[]] - Array of route paths to exclude from site/locale expansion.
 * @returns {Array<Object>} An array of route objects with expanded paths for each site and locale as configured.
 */
export const configureRoutes = (routes = [], config, {ignoredRoutes = []}) => {
    if (!routes.length || !config) return routes

    const {url: urlConfig} = config.app
    const allSites = getSites()
    if (!allSites) return routes

    const outputRoutes = new Map()

    const addRoute = (path, routeRest) => {
        if (!outputRoutes.has(path)) {
            outputRoutes.set(path, {path, ...routeRest})
        }
    }

    routes.forEach(({path, ...routeRest}) => {
        if (ignoredRoutes.includes(path)) {
            addRoute(path, routeRest)
        } else {
            allSites.forEach((site) => {
                const siteRefs = [site.alias, site.id].filter(Boolean)
                const localeRefs = site.l10n.supportedLocales
                    .flatMap((locale) => [locale.alias, locale.id])
                    .filter(Boolean)

                routes.forEach(({path: routePath, ...routeRest}) => {
                    const {locale: localePosition, site: sitePosition} = urlConfig

                    if (
                        localePosition === urlPartPositions.PATH &&
                        sitePosition === urlPartPositions.PATH
                    ) {
                        siteRefs.forEach((site) => {
                            addRoute(`/${site}${routePath}`, routeRest)
                            localeRefs.forEach((locale) => {
                                addRoute(`/${site}/${locale}${routePath}`, routeRest)
                                addRoute(`/${locale}${routePath}`, routeRest)
                            })
                        })
                    } else if (sitePosition === urlPartPositions.PATH) {
                        siteRefs.forEach((site) => addRoute(`/${site}${routePath}`, routeRest))
                    } else if (localePosition === urlPartPositions.PATH) {
                        localeRefs.forEach((locale) =>
                            addRoute(`/${locale}${routePath}`, routeRest)
                        )
                    }
                })
            })
            addRoute(path, routeRest)
        }
    })

    return Array.from(outputRoutes.values())
}
